import { useRef, useState, useLayoutEffect } from 'react';
import { cn } from '@simplywallst/ui-core/next';
import { clamp } from '../utils';
import { useClampWithinElement } from '../hooks/useClampWithinElement';
import type { ChartOverlayProps } from './ChartOverlay';

const TooltipStatic = ({
  top,
  tooltipRef,
  className,
  children,
}: {
  top: number;
  children: React.ReactNode;
  tooltipRef?: React.RefObject<HTMLDivElement>;
  className?: string;
}) => (
  <div
    className={cn(
      'absolute right-0 -translate-y-1/2 overflow-hidden text-[--chart-price-color] backdrop-blur-sm',
      className
    )}
    style={{ top: `${top}px` } as React.CSSProperties}
    ref={tooltipRef}
  >
    <div className="absolute inset-0 rounded-sm bg-current opacity-50 brightness-[.15] saturate-[.25]" />
    <div className="relative px-[5px] py-1 text-xs font-semibold leading-none tracking-wide">
      {children}
    </div>
  </div>
);

const TooltipsStaticWrapper = ({
  children,
  isMarkerPinned,
}: {
  children: React.ReactNode;
  isMarkerPinned: boolean;
}) => (
  <div
    className={cn(
      'pointer-events-none absolute inset-y-0 transition-opacity duration-150 ease-out group-hover/chart:opacity-0',
      'right-[--chart-padding] mr-2 text-right',
      isMarkerPinned && 'opacity-0'
    )}
  >
    {children}
  </div>
);

type TooltipData = {
  originalY: number;
  type: 'price' | 'fairValue';
  index: number;
};

type AdjustedTooltipData = TooltipData & { adjustedY: number };

const getNonOverlappingTooltips = ({
  minDistance = 0,
  items,
}: {
  minDistance?: number;
  items: TooltipData[];
}) => {
  // Keep items in original order - first item never moves, subsequent items adjust
  const adjustedTooltips = items.reduce((acc, current, index) => {
    if (index === 0) {
      // First item never moves from its original position
      acc.push({ ...current, adjustedY: current.originalY });
      return acc;
    }

    // For subsequent items, find a position that doesn't overlap with any previous items
    let proposedY = current.originalY;
    for (const previous of acc) {
      if (Math.abs(proposedY - previous.adjustedY) < minDistance) {
        // Move below the overlapping item
        proposedY = Math.max(proposedY, previous.adjustedY + minDistance);
      }
    }

    acc.push({ ...current, adjustedY: proposedY });
    return acc;
  }, [] as AdjustedTooltipData[]);

  // Find adjusted positions for price and fair value tooltips
  const priceTooltip = adjustedTooltips.find((t) => t.type === 'price');
  const fairValueTooltips = adjustedTooltips.filter(
    (t) => t.type === 'fairValue'
  );

  return { priceTooltip, fairValueTooltips };
};

export const TooltipsStatic = ({
  lastData,
  height,
  isMarkerPinned,
  narrativeLabels,
}: Pick<ChartOverlayProps, 'lastData' | 'height' | 'isMarkerPinned'> & {
  narrativeLabels: string[];
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipHeight, setTooltipHeight] = useState(24); // Default fallback

  // Measure tooltip height (assumes all tooltip heights match)
  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const height = tooltipRef.current.offsetHeight;
    if (height <= 0) return;
    setTooltipHeight(height);
  }, []);

  if (lastData.fairValueYs.length === 0) {
    return (
      <TooltipsStaticWrapper isMarkerPinned={isMarkerPinned}>
        <TooltipStatic
          top={clamp(lastData.priceY ?? 0, height - tooltipHeight / 2)}
          tooltipRef={tooltipRef}
        >
          <span className="sr-only">Price </span>
          {lastData.price}
        </TooltipStatic>
      </TooltipsStaticWrapper>
    );
  }

  // To avoid overlapping tooltips, we need to adjust the positions of the tooltips
  // so that they are spaced out by at least the height of the tooltip + 2px.
  const { priceTooltip, fairValueTooltips } = getNonOverlappingTooltips({
    minDistance: tooltipHeight + 2,
    items: [
      { originalY: lastData.priceY ?? 0, type: 'price' as const, index: -1 },
      ...lastData.fairValueYs.map((y, index) => ({
        originalY: y,
        type: 'fairValue' as const,
        index,
      })),
    ],
  });

  return (
    <TooltipsStaticWrapper isMarkerPinned={isMarkerPinned}>
      <TooltipStatic
        top={clamp(
          priceTooltip?.adjustedY ?? lastData.priceY ?? 0,
          height - tooltipHeight / 2
        )}
        tooltipRef={tooltipRef}
      >
        <span className="sr-only">Price </span>
        {lastData.price}
      </TooltipStatic>
      {lastData.fairValueYs.map((y, index) => {
        const adjustedTooltip = fairValueTooltips.find(
          (t) => t.index === index
        );
        return (
          <TooltipStatic
            key={index}
            top={clamp(
              adjustedTooltip?.adjustedY ?? y,
              height - tooltipHeight / 2
            )}
            className={cn(
              'whitespace-nowrap text-[--chart-valuation-color] opacity-0 transition-opacity duration-150 ease-out',
              index === lastData.fairValueIndex && 'opacity-100'
            )}
          >
            <span className="sr-only">{narrativeLabels[index]} </span>
            {lastData.fairValues[index]}
          </TooltipStatic>
        );
      })}
    </TooltipsStaticWrapper>
  );
};

export const TooltipsInteractive = ({
  isMarkerPinned,
  selectedData,
  height,
  intrinsicDiscount,
  mouseY,
}: Required<Pick<ChartOverlayProps, 'selectedData' | 'isMarkerPinned'>> & {
  height: number;
  intrinsicDiscount: React.ReactNode;
  mouseY: number;
}) => {
  const { clampedEl, x, y, hasTouchedLeftBound } = useClampWithinElement({
    mouseX: selectedData.x,
    mouseY,
    height,
  });
  return (
    <div
      className={cn(
        'pointer-events-none absolute opacity-0 group-hover/chart:opacity-100 md:min-w-[30ch]',
        hasTouchedLeftBound ? 'left-0 ml-5' : 'right-full mr-5',
        isMarkerPinned && 'md:opacity-100'
      )}
      style={{ transform: `translate(${x}px, ${y}px)` } as React.CSSProperties}
    >
      <div
        className="overflow-hidden rounded px-2 py-1 backdrop-blur-sm md:px-2.5 md:py-2"
        ref={clampedEl}
      >
        <div className="absolute inset-0 bg-surface opacity-75" />
        <div className="relative grid gap-2">
          <time
            className="min-w-[12ch] whitespace-nowrap text-right text-tiny text-softer"
            dateTime={selectedData.date}
          >
            {selectedData.date}
          </time>
          <div className="max-md:hidden">
            <div className="text-sm">
              {selectedData.fairValues[selectedData.fairValueIndex] && (
                <div className="flex justify-between gap-1.5 whitespace-nowrap">
                  <span>Fair Value</span>
                  <span className="text-right font-semibold text-[--chart-valuation-color]">
                    {selectedData.fairValues[selectedData.fairValueIndex]}
                  </span>
                </div>
              )}
              <div className="flex justify-between gap-1.5 whitespace-nowrap">
                <span>Share Price</span>
                <span className="text-right font-semibold text-[--chart-price-color]">
                  {selectedData.price}
                </span>
              </div>
            </div>
            <div className="text-right [&_*]:text-softer">
              {intrinsicDiscount}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
